import { IOfficeAccountingTableRow } from "@/src/types/officeAccounting";

/** Columns summed into Total Amount USD (Preparatory Department → Hostel Accommodation). */
export const OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS = [
  "preparatoryDepartment",
  "costFirstSemester",
  "costSecondSemester",
  "costThirdSemester",
  "costFouthSemester",
  "costFifthSemester",
  "costSixthSemester",
  "costSeventhSemester",
  "costEighthSemester",
  "costNinthSemester",
  "costTenthSemester",
  "costEleventhSemester",
  "costTwelfthSemester",
  "pgPhdCostFirstSemester",
  "pgPhdCostSecondSemester",
  "pgPhdCostThirdSemester",
  "pgPhdCostFourthSemester",
  "pgPhdCostFifthSemester",
  "pgPhdCostSixthSemester",
  "otherExpenses",
  "transfer",
  "insurance",
  "admissionFee",
  "temporaryResidencePermission",
  "hostelAccommodation",
] as const;

export type OfficeAccountingTotalAmountUsdSourceColumn =
  (typeof OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS)[number];

const SOURCE_COLUMN_SET = new Set<string>(OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS);

export const isOfficeAccountingTotalAmountUsdSourceColumn = (columnId: string): boolean =>
  SOURCE_COLUMN_SET.has(columnId);

const stripCurrencyNoise = (raw: string): string =>
  raw
    .replace(/USD|EUR|UAH/gi, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .trim();

/** Parse display/API money strings like `1 200.00 USD`, `1200`, `=100+50`. */
export const parseOfficeAccountingAmount = (raw: string | null | undefined): number => {
  const original = String(raw ?? "").trim();
  if (!original || original === "—" || original === "-") return 0;

  const cleaned = stripCurrencyNoise(original.replace(/^=/, ""));
  if (!cleaned) return 0;

  if (/^[\d+\-*/.()]+$/.test(cleaned)) {
    try {
      const result = Function(`"use strict"; return (${cleaned})`)();
      return typeof result === "number" && Number.isFinite(result) ? result : 0;
    } catch {
      return 0;
    }
  }

  const match = cleaned.match(/-?\d+(\.\d+)?/);
  if (!match) return 0;
  const parsed = Number.parseFloat(match[0]);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatOfficeAccountingUsdAmount = (amount: number): string => {
  const safe = Number.isFinite(amount) ? amount : 0;
  const [intPart, decPart] = safe.toFixed(2).split(".");
  const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${withSpaces}.${decPart} USD`;
};

/** @deprecated Use formatOfficeAccountingUsdAmount */
export const formatOfficeAccountingTotalAmountUsd = formatOfficeAccountingUsdAmount;

const toAmountRaw = (value: unknown): string => {
  if (value == null) return "";
  if (Array.isArray(value)) return value.map(String).join(" ");
  return String(value);
};

export const computeOfficeAccountingTotalAmountUsd = (
  row: IOfficeAccountingTableRow,
  visibleColumnIds: ReadonlySet<string> | readonly string[],
): string => {
  const visible =
    visibleColumnIds instanceof Set ? visibleColumnIds : new Set(visibleColumnIds);

  let sum = 0;
  for (const columnId of OFFICE_ACCOUNTING_TOTAL_AMOUNT_USD_SOURCE_COLUMNS) {
    if (!visible.has(columnId)) continue;
    sum += parseOfficeAccountingAmount(row[columnId as keyof IOfficeAccountingTableRow] as string);
  }

  return formatOfficeAccountingUsdAmount(sum);
};

/** Debt Balance USD = Payment Fact USD − Total Amount USD */
export const computeOfficeAccountingDebtBalanceUsd = (row: IOfficeAccountingTableRow): string => {
  const paymentFact = parseOfficeAccountingAmount(toAmountRaw(row.paymentFactUsd));
  const totalAmount = parseOfficeAccountingAmount(row.totalAmountUsd);
  return formatOfficeAccountingUsdAmount(paymentFact - totalAmount);
};

export const applyOfficeAccountingDerivedUsdAmounts = <T extends IOfficeAccountingTableRow>(
  rows: T[],
  visibleColumnIds: ReadonlySet<string> | readonly string[],
): T[] =>
  rows.map((row) => {
    const totalAmountUsd = computeOfficeAccountingTotalAmountUsd(row, visibleColumnIds);
    return {
      ...row,
      totalAmountUsd,
      debtBalanceUsd: computeOfficeAccountingDebtBalanceUsd({ ...row, totalAmountUsd }),
    };
  });

/** @deprecated Use applyOfficeAccountingDerivedUsdAmounts */
export const applyOfficeAccountingTotalAmountUsd = applyOfficeAccountingDerivedUsdAmounts;
